import React, { useEffect, useState } from "react";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Hidden } from "@material-ui/core";

import MenuDetailsView from "./components/MenuDetailsView";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../../utils/consts";
import PageHeaderMobile from "../../../../../components/common/PageHeader/PageHeaderMobile";
import Authorities from "../../../../../auth/authorities";
import withAuthority from "../../../../../components/Auth/withAuthority";
import DefaultAlert from "../../../../../components/alerts/DefaultAlert";
import { getLocalStore } from "../../../../../utils/store/localStore";

// Import modals for directory and product editing
import { useForm } from "react-hook-form";
import DirectoryEditModal from "../../MenusNewTest/Modal/DirectoryEditModal";
import ProductEditModal from "../../MenusNewTest/Modal/ProductEditModal";
import DirectoryOrProductSelectionModal from "../../MenusNewTest/Modal/DirectoryOrProductSelectionModal";
import {
  fetchAllMenuDetails,
  fetchMenuDetails,
  fetchMenus,
  setProductsVersion,
} from "../../../../../redux/actions/menu";
import ProductAddModal from "./components/ProductAddModal";
import DirectoryAddModal from "./components/DirectoryAddModal";
import TextSkeletonLoading from "../../../../../components/common/Loading/TextSkeletonLoading";
import { fetchProductConfigInfo } from "../../../../../services/menuApp/printerMenuService";
import { AxiosError } from "axios";
import EmptyModal from "../../MenusNewTest/Modal/EmptyModal";
import { fetchTags } from "../../../../../services/menuApp/categoryMenuService";

interface Params {
  menuId: string;
  locationId: string;
}

const MenuDetails: React.FunctionComponent = () => {
  const { menuId, locationId } = useParams<Params>();
  const match: any = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();

  // Safe useSelector with default values
  const reduxState = useSelector((state: any) => state || {});
  const menuState = reduxState.menu || {};
  const { menuDetails = {}, isLoading = false } = menuState;
  const menus = useSelector((state: any) => state.menu?.menus || []);

  // Get current menu data from Redux store (already loaded by MenuList)
  const currentMenuData = menuDetails[menuId];

  const [menuNodeList, setMenuNodeList] = useState<any>(
    currentMenuData?.menuNodeList || {},
  );
  const [selectedMenuName, setSelectedMenuName] = useState(
    currentMenuData?.selectedMenuName || "",
  );
  const [error, setError] = useState("");
  const [categoryNode, setCategoryNode] = useState<any>(
    currentMenuData?.categoryNode || [],
  );
  const [productVersion, setProductVersion] = useState<any>(
    currentMenuData?.productVersion,
  );
  const [tagsState, setTagsState] = useState<any>(currentMenuData?.tags || {});
  const [stockDepartmentSelectedNode, setStockDepartmentSelectedNode] =
    useState<any>(currentMenuData?.stockDepartmentSelectedNode || []);
  const [stockGroupSelectedNode, setStockGroupSelectedNode] = useState<any>(
    currentMenuData?.stockGroupSelectedNode || [],
  );
  const [selectedGroupObj, setSelectedGroupObj] = useState<any>(
    currentMenuData?.selectedGroupObj || {},
  );
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  // Directory Edit Modal States
  const [isOpenDirectoryEditModal, setIsOpenDirectoryEditModal] =
    useState(false);
  const [selectedDirectory, setSelectedDirectory] = useState<any>({});
  const [selectedDirectoryCategory, setSelectedDirectoryCategory] =
    useState<any>({});
  const [directoryIsRoot, setDirectoryIsRoot] = useState(false);
  const [directoryCategoryId, setDirectoryCategoryId] = useState("");
  const [directoryWrapperId, setDirectoryWrapperId] = useState("");
  const [directoryFirstWrapperId, setDirectoryFirstWrapperId] = useState("");
  const [isOpenStackProductModal, setIsOpenStackProductModal] = useState(false);
  // Directory form states
  const [directoryName, setDirectoryName] = useState("");
  const [directoryUrl, setDirectoryUrl] = useState("");
  const [directoryColor, setDirectoryColor] = useState("");
  const [isDirectoryNameEmpty, setIsDirectoryNameEmpty] = useState(false);
  const [isLoadingDirectoryButton, setIsLoadingDirectoryButton] =
    useState(false);
  const [isStack, setIsStack] = useState(false);
  // Product Edit Modal States
  const [isOpenProductEditModal, setIsOpenProductEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>({});
  const [selectedProductCategory, setSelectedProductCategory] = useState<any>(
    {},
  );
  const [selectedCatMenuTitle, setSelectedCatMenuTitle] = useState("");
  const [productIsRoot, setProductIsRoot] = useState(false);
  const [productCategoryId, setProductCategoryId] = useState("");
  const [productWrapperId, setProductWrapperId] = useState("");
  const [productFirstWrapperId, setProductFirstWrapperId] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [printerMappingItemName, setPrinterMappingItemName] = useState(true);
  const [
    selectedPrinterMappingItemOrCategoryId,
    setSelectedPrinterMappingItemOrCategoryId,
  ] = useState("");
  const [isOpenMappingModal, setIsOpenMappingModal] = useState(false);
  const [stackNode, setStackNode] = useState<any>([]);
  const [isLoadingMappingModal, setIsLoadingMappingModal] = useState(true);
  const [categoryName, setCategoryName] = useState("");
  // Add New Item Modal States
  const [
    isOpenDirectoryOrProductSelectionModal,
    setIsOpenDirectoryOrProductSelectionModal,
  ] = useState(false);
  const [selectedCategoryForAdd, setSelectedCategoryForAdd] =
    useState<any>(null);
  const [selectedWrapperForAdd, setSelectedWrapperForAdd] = useState<any>(null);
  const [isOpenDirectoryAddModal, setIsOpenDirectoryAddModal] = useState(false);
  const [isOpenProductAddModal, setIsOpenProductAddModal] = useState(false);
  const [availableTags, setAvailableTags] = useState<any>([]);
  const [type, setType] = useState("");

  // Form hooks
  const { register, handleSubmit, errors, reset, setValue } = useForm<any>();

  const productsVersion = useSelector(
    (state: any) => state.menu?.productsVersion || 1,
  );
  const isEditDisabled = productsVersion === 1;

  // ✅ Fetch tags when component mounts
  useEffect(() => {
    const loadTags = async () => {
      try {
        const res = await fetchTags(locationId);
        console.log("Fetched tags from API:", res?.data?.data);
        if (res?.data?.data) {
          const tagsArray = Object.values(res.data.data);
          setAvailableTags(tagsArray);
          console.log("Tags set to state:", tagsArray);
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    loadTags();
  }, [locationId]);

  const getProductConfiguration = async () => {
    if (!locationId) return;

    try {
      const { data } = await fetchProductConfigInfo(locationId);
      if (data && data.data) {
        const version = data.data.productsVersion || 1;
        setProductsVersion(version);
        // Dispatch to Redux
        dispatch(setProductsVersion(version));
      }
    } catch (error) {
      const err: any = error as AxiosError;
      if (err.response?.status !== 404) {
        console.error("Error fetching product configuration:", error);
      }
    } finally {
    }
  };

  // Function to load menu details - will be called when needed
  const loadMenuDetails = async () => {
    if (hasAttemptedFetch) return;

    setIsDataLoading(true);
    setHasAttemptedFetch(true);

    try {
      console.log("Fetching menu details for menuId:", menuId);
      // First ensure menus are loaded
      if (!menus || menus.length === 0) {
        await dispatch(fetchMenus(locationId));
      }
      // Fetch menu details
      await dispatch(fetchMenuDetails(locationId, menuId));

      await getProductConfiguration();

      // Small delay to ensure Redux update
      setTimeout(() => {
        setIsDataLoading(false);
        setIsInitialLoad(false);
      }, 100);
    } catch (error) {
      console.error("Error loading menu details:", error);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsDataLoading(false);
      setIsInitialLoad(false);
    }
  };

  // Check if data exists in Redux and load if needed
  useEffect(() => {
    const currentData = menuDetails[menuId];
    console.log(
      "Fetching menu details for menuId: 222222",
      menuId,
      locationId,
      menuDetails,
      hasAttemptedFetch,
    );

    if (currentData?.menuNodeList) {
      // Data exists in Redux, use it immediately
      console.log("Using cached menu details from Redux");
      setMenuNodeList(currentData.menuNodeList || {});
      setSelectedMenuName(currentData.selectedMenuName || "");
      setCategoryNode(currentData.categoryNode || []);
      setProductVersion(currentData.productVersion);
      setTagsState(currentData.tags || {});
      setStockDepartmentSelectedNode(
        currentData.stockDepartmentSelectedNode || [],
      );
      setStockGroupSelectedNode(currentData.stockGroupSelectedNode || []);
      setSelectedGroupObj(currentData.selectedGroupObj || {});
      setIsDataLoading(false);
      setIsInitialLoad(false);
      setHasAttemptedFetch(false);
    } else if (!hasAttemptedFetch && !isInitialLoad) {
      // Data not in Redux, fetch it (but only once)
      console.log(
        "Fetching menu details for menuId:",
        hasAttemptedFetch,
        isInitialLoad,
      );
      loadMenuDetails();
    } else if (isInitialLoad) {
      // First time loading, check if we need to fetch
      const checkData = async () => {
        if (!currentData?.menuNodeList && !hasAttemptedFetch) {
          console.log(
            "Fetching menu details for menuId: 3333",
            hasAttemptedFetch,
            currentData?.menuNodeList,
          );
          await loadMenuDetails();
        } else {
          setIsInitialLoad(false);
        }
      };
      checkData();
    }
  }, [menuId, locationId]);

  // ✅ FIXED: Update local state when Redux data changes (removed isDataLoading condition)
  useEffect(() => {
    const currentData = menuDetails[menuId];
    if (currentData?.menuNodeList) {
      console.log("Redux data updated, syncing local state");
      setMenuNodeList(currentData.menuNodeList || {});
      setSelectedMenuName(currentData.selectedMenuName || "");
      setCategoryNode(currentData.categoryNode || []);
      setProductVersion(currentData.productVersion);
      setTagsState(currentData.tags || {});
      setStockDepartmentSelectedNode(
        currentData.stockDepartmentSelectedNode || [],
      );
      setStockGroupSelectedNode(currentData.stockGroupSelectedNode || []);
      setSelectedGroupObj(currentData.selectedGroupObj || {});
      setIsDataLoading(false);
      setIsInitialLoad(false);
    }
  }, [menuDetails, menuId]); // ✅ Removed isDataLoading dependency

  useEffect(() => {
    document.title = `Menu - ${selectedMenuName || "Details"}`;
    setSelectedCatMenuTitle(selectedMenuName);
  }, [selectedMenuName]);

  const handleBackToMenus = () => {
    const basePath = match.url.split(`/${menuId}`)[0];
    history.push(basePath);
  };

  // Handle reorder complete - refresh menu details in Redux
  const handleReorderComplete = () => {
    // Refresh menu details in Redux cache
    dispatch(fetchMenuDetails(locationId, menuId));
  };

  // Directory Edit Modal Handlers
  const handleOpenDirectoryEditModal = (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModalVal: any,
    isRootValue: any,
  ) => {
    console.log("Opening directory edit modal for:", product);
    setSelectedDirectory(product);
    setSelectedDirectoryCategory(category);
    setDirectoryCategoryId(category.id);
    setDirectoryWrapperId(product.id);
    setDirectoryFirstWrapperId(wrapper?.id || product.id);
    setDirectoryIsRoot(isRootValue);

    setDirectoryName(product.name || "");
    setDirectoryUrl(product.img || "");
    setDirectoryColor(product.colour || "");
    setIsDirectoryNameEmpty(false);

    setIsOpenDirectoryEditModal(true);
  };

  const handleCloseDirectoryEditModal = () => {
    setIsOpenDirectoryEditModal(false);
    setSelectedDirectory({});
    setSelectedDirectoryCategory({});
    setDirectoryCategoryId("");
    setDirectoryWrapperId("");
    setDirectoryFirstWrapperId("");
    setDirectoryName("");
    setDirectoryUrl("");
    setDirectoryColor("");
    setIsDirectoryNameEmpty(false);

    // Refresh data after directory edit
    dispatch(fetchMenuDetails(locationId, menuId));
  };

  // Product Edit Modal Handlers
  const handleOpenProductEditModal = (
    product: any,
    wrapper: any,
    category: any,
    isOpenWrapperEditModalVal: any,
    isRootValue: any,
  ) => {
    console.log("Opening product edit modal for:", product);
    setSelectedProduct(product);
    setSelectedProductCategory(category);
    setProductCategoryId(category.id);
    setProductWrapperId(product.id);
    setProductFirstWrapperId(wrapper?.id || product.id);
    setProductIsRoot(isRootValue);
    setIsOpenProductEditModal(true);
  };

  const handleCloseProductEditModal = () => {
    setIsOpenProductEditModal(false);
    setSelectedProduct({});
    setSelectedProductCategory({});
    setProductCategoryId("");
    setProductWrapperId("");
    setProductFirstWrapperId("");

    // Refresh data after product edit
    dispatch(fetchMenuDetails(locationId, menuId));
  };

  // Add New Item Modal Handlers
  const handleOpenProductOrDirectoryModals = (category: any, wrapper?: any) => {
    console.log("Open product or directory modal", category, wrapper);
    setSelectedCategoryForAdd(category);
    setSelectedWrapperForAdd(wrapper || null);
    setIsOpenDirectoryOrProductSelectionModal(true);
  };

  const handleOpenDirectoryModalFromSelection = () => {
    setIsOpenDirectoryOrProductSelectionModal(false);
    setIsOpenDirectoryAddModal(true);
  };

  const handleOpenProductModalFromSelection = () => {
    setIsOpenDirectoryOrProductSelectionModal(false);
    setIsOpenProductAddModal(true);
  };

  const handleCloseDirectoryAddModal = () => {
    setIsOpenDirectoryAddModal(false);
    // Refresh data after directory add
    dispatch(fetchMenuDetails(locationId, menuId));
  };

  const handleCloseProductAddModal = () => {
    setIsOpenProductAddModal(false);
    // Refresh data after product add
    dispatch(fetchMenuDetails(locationId, menuId));
  };

  const handleCloseSelectionModal = () => {
    setIsOpenDirectoryOrProductSelectionModal(false);
  };

  // Other modal handlers

  const handleOpenMappingModal = (
    event: any,
    category: any,
    item: any,
    categoryOrItem: string,
    isMap?: any,
  ) => {
    if (event) {
      event.stopPropagation();
    }
    setIsOpenModal(true);
    setIsLoadingMappingModal(true);
    setCategoryName(category?.name || "");
    setPrinterMappingItemName(item?.name || "");
    setType(categoryOrItem);
    setIsStack(categoryOrItem === "item" && !!isMap);
    setSelectedPrinterMappingItemOrCategoryId(item?.id);
    setIsLoadingMappingModal(false);
    setIsOpenStackProductModal(false);
    setIsOpenMappingModal(true);
  };

  const setIsProductAdd = (value: any) => {
    console.log("Set is product add", value);
  };

  const getAllStockDepartments = async (): Promise<void> => {};
  const isOpenWrapperEditModal = false;
  const isRoot = false;
  const firstWrapperId = "";
  const wrapperId = "";
  const removedLinkedIdLists = {};
  const handleClickLinkedId = (
    e: any,
    category: any,
    categoryIdValue: any,
  ) => {};
  const handleClearStrip = (product: any) => {};
  const handleNavigate = (product: any, level: any, data: any) => {};
  console.log(
    "afgsdgsfafasffsdgg",
    isDataLoading,
    isInitialLoad,
    !currentMenuData?.menuNodeList,
  );
  // Show loading while data is being fetched
  if (isDataLoading || (isInitialLoad && !currentMenuData?.menuNodeList)) {
    return <TextSkeletonLoading />;
  }

  // ✅ Get existing products for selected category (for viewOrder calculation)
  const getExistingProductsForCategory = () => {
    if (!selectedCategoryForAdd) return [];
    const category = selectedCategoryForAdd;
    if (category?.wrapper) {
      return Object.values(category.wrapper);
    }
    return [];
  };

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>
      <MenuDetailsView
        menuNodeList={menuNodeList}
        selectedMenuId={menuId}
        selectedMenuName={selectedMenuName}
        onBack={handleBackToMenus}
        locationId={locationId}
        onMenuUpdate={() => dispatch(fetchMenuDetails(locationId, menuId))}
        isLoadingDetails={isLoading}
        setMenuNodeList={setMenuNodeList}
        baseUrl={match.url.split(`/${menuId}`)[0]}
        categoryNode={categoryNode}
        productVersion={productVersion}
        tags={tagsState}
        stockDepartmentSelectedNode={stockDepartmentSelectedNode}
        stockGroupSelectedNode={stockGroupSelectedNode}
        selectedGroupObj={selectedGroupObj}
        setSelectedGroupObj={setSelectedGroupObj}
        setCategoryNode={setCategoryNode}
        setTags={setTagsState}
        setStockDepartmentSelectedNode={setStockDepartmentSelectedNode}
        setStockGroupSelectedNode={setStockGroupSelectedNode}
        setProductVersion={setProductVersion}
        // Directory Edit Modal Props
        handleOpenDirectoryEditModal={handleOpenDirectoryEditModal}
        handleOpenProductEditModal={handleOpenProductEditModal}
        handleOpenMappingModal={handleOpenMappingModal}
        handleOpenProductOrDirectoryModals={handleOpenProductOrDirectoryModals}
        setIsProductAdd={setIsProductAdd}
        setError={setError}
        isOpenWrapperEditModal={isOpenWrapperEditModal}
        isRoot={isRoot}
        firstWrapperId={firstWrapperId}
        wrapperId={wrapperId}
        removedLinkedIdLists={removedLinkedIdLists}
        handleClickLinkedId={handleClickLinkedId}
        handleClearStrip={handleClearStrip}
        handleNavigate={handleNavigate}
        onReorderComplete={handleReorderComplete}
        isEditDisabled={isEditDisabled}
      />

      {/* Directory Or Product Selection Modal */}
      <DirectoryOrProductSelectionModal
        isOpen={isOpenDirectoryOrProductSelectionModal}
        setIsOpen={setIsOpenDirectoryOrProductSelectionModal}
        handleOpenDirectoryModal={handleOpenDirectoryModalFromSelection}
        handleOpenProductModal={handleOpenProductModalFromSelection}
        handleCloseDirectoryOrProductSelectionModal={handleCloseSelectionModal}
      />

      {/* Directory Add Modal */}
      <DirectoryAddModal
        isOpen={isOpenDirectoryAddModal}
        setIsOpen={setIsOpenDirectoryAddModal}
        handleCloseDirectoryAddModal={handleCloseDirectoryAddModal}
        categoryId={selectedCategoryForAdd?.id}
        directoryId={selectedWrapperForAdd?.id}
        directoryLevel={1}
        menuNodeList={menuNodeList}
        menuId={menuId}
        firstWrapperId={selectedWrapperForAdd?.id}
        setMenuNodeList={setMenuNodeList}
        setSuccess={() => {}}
        setError={setError}
        isRoot={!selectedWrapperForAdd}
        setIsOpenDirectoryOrProductSelectionModal={
          setIsOpenDirectoryOrProductSelectionModal
        }
        onItemAdded={() => dispatch(fetchMenuDetails(locationId, menuId))}
        wrapperId={selectedWrapperForAdd?.id}
      />

      {/* ✅ FIXED: Product Add Modal with selectedProduct prop */}
      <ProductAddModal
        isOpen={isOpenProductAddModal}
        setIsOpen={setIsOpenProductAddModal}
        handleCloseProductAddModal={handleCloseProductAddModal}
        stockDepartmentSelectedNode={stockDepartmentSelectedNode}
        categoryId={selectedCategoryForAdd?.id}
        directoryId={selectedWrapperForAdd?.id}
        directoryLevel={1}
        menuNodeList={menuNodeList}
        selectedMenuId={menuId}
        firstWrapperId={selectedWrapperForAdd?.id}
        setMenuNodeList={setMenuNodeList}
        setSuccess={() => {}}
        setError={setError}
        isRoot={!selectedWrapperForAdd}
        menuId={menuId}
        setIsOpenDirectoryOrProductSelectionModal={
          setIsOpenDirectoryOrProductSelectionModal
        }
        onItemAdded={() => dispatch(fetchMenuDetails(locationId, menuId))}
        wrapperId={selectedWrapperForAdd?.id}
        availableTags={availableTags}
        // ✅ ADDED: Pass existing products for viewOrder calculation
        selectedProduct={getExistingProductsForCategory()}
      />

      {/* Directory Edit Modal */}
      <DirectoryEditModal
        isOpen={isOpenDirectoryEditModal}
        setIsOpen={setIsOpenDirectoryEditModal}
        setDirectoryName={setDirectoryName}
        directoryName={directoryName}
        isDirectoryNameEmpty={isDirectoryNameEmpty}
        setIsDepartmentEmpty={setIsDirectoryNameEmpty}
        handleCloseDirectoryEditModal={handleCloseDirectoryEditModal}
        setUrl={setDirectoryUrl}
        isLoadingButton={isLoadingDirectoryButton}
        url={directoryUrl}
        setIsLoadingButton={setIsLoadingDirectoryButton}
        categoryId={directoryCategoryId}
        directoryId={directoryWrapperId}
        directoryLevel={1}
        setSuccess={() => {}}
        setError={setError}
        setIsDirectoryNameEmpty={setIsDirectoryNameEmpty}
        menuNodeList={menuNodeList}
        wrapperId={directoryWrapperId}
        menuId={menuId}
        firstWrapperId={directoryFirstWrapperId}
        setMenuNodeList={setMenuNodeList}
        isRoot={directoryIsRoot}
        setIsOpenDirectoryOrProductSelectionModal={() => {}}
        selectedWrapper={selectedDirectory}
        setColor={setDirectoryColor}
        color={directoryColor}
        onDirectoryUpdated={() =>
          dispatch(fetchMenuDetails(locationId, menuId))
        }
        setCurrentContent={() => {}}
        setCurrentDirectoryStack={() => {}}
        currentDirectoryStack={[]}
        isEditDisabled={isEditDisabled}
      />

      {/* Product Edit Modal */}
      <ProductEditModal
        isOpen={isOpenProductEditModal}
        setIsOpen={setIsOpenProductEditModal}
        handleCloseProductEditModal={handleCloseProductEditModal}
        isLoadingButton={false}
        errors={errors}
        register={register}
        handleOpenDepartmentAddModal={() => {}}
        stockDepartmentSelectedNode={stockDepartmentSelectedNode}
        selectedDepartmentObj={{}}
        setSelectedDepartmentObj={() => {}}
        setDepartmentName={() => {}}
        isDepartmentEmpty={false}
        setIsDepartmentEmpty={() => {}}
        reset={reset}
        isValidProduct={true}
        setIsValidProduct={() => {}}
        inTax={{}}
        setInTax={() => {}}
        isByo={false}
        setIsByo={() => {}}
        isCustomisationPriority={false}
        setIsCustomisationPriority={() => {}}
        setIsMirrored={() => {}}
        isSpicySelectionEnabled={false}
        setIsSpicySelectionEnabled={() => {}}
        outTax={{}}
        setOutTax={() => {}}
        allYouCanEat={false}
        setAllYouCanEat={() => {}}
        setColor={() => {}}
        color=""
        setIsValidKiosk={() => {}}
        isValidKiosk={true}
        isDirty={false}
        selectedWrapper={selectedProduct}
        setError={setError}
        productId={productWrapperId}
        menuNodeList={menuNodeList}
        selectedMenuId={menuId}
        categoryId={productCategoryId}
        wrapperId={productWrapperId}
        setMenuNodeList={setMenuNodeList}
        setIsOpenProductEditModal={setIsOpenProductEditModal}
        setIsLoadingButton={() => {}}
        handleSubmit={handleSubmit}
        menuId={menuId}
        firstWrapperId={productFirstWrapperId}
        isRoot={productIsRoot}
        getAllStockDepartments={getAllStockDepartments}
        refreshPrices={async () => {}}
        departmentList={[]}
        setDepartmentList={() => {}}
      />

      <EmptyModal
        isOpen={isOpenModal}
        setIsOpen={setIsOpenModal}
        isOpenMappingModal={isOpenMappingModal}
        setError={setError}
        printerMappingItemName={printerMappingItemName}
        selectedPrinterMappingItemOrCategoryId={
          selectedPrinterMappingItemOrCategoryId
        }
        type={type}
        selectedCatMenuTitle={selectedCatMenuTitle}
        categoryName={categoryName}
        isLoading={isLoadingMappingModal}
        setIsLoading={setIsLoadingMappingModal}
        isOpenStackProductModal={isOpenStackProductModal}
        setIsOpenStackProductModal={setIsOpenStackProductModal}
        stackNode={stackNode}
        handleOpenMappingModal={handleOpenMappingModal}
        setIsOpenMappingModal={setIsOpenMappingModal}
        isStack={isStack}
        isEditDisabled={isEditDisabled}
      />

      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(MenuDetails, Authorities.MENU_READ);
